import { useState } from "react";
import AuthLayout from "../components/AuthLayout";
import { supabase } from "../services/supabase";
import {
  validateEmail,
  validatePassword,
  validateConfirmPassword,
} from "../utils/validators";
import { Check } from "lucide-react";

export default function Register({
  onBack,
  onRegisterSuccess,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [strength, setStrength] = useState(0);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const calculateStrength = (pwd) => {
    let str = 0;
    if (pwd.length >= 8) str += 25;
    if (pwd.length >= 12) str += 25;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) str += 25;
    if (/[0-9]/.test(pwd)) str += 15;
    if (/[^a-zA-Z0-9]/.test(pwd)) str += 10;
    return Math.min(str, 100);
  };

  const handlePasswordChange = (e) => {
    const pwd = e.target.value;
    setPassword(pwd);
    setStrength(calculateStrength(pwd));
  };

  const getStrengthLabel = () => {
    if (strength >= 80) return "Excellent";
    if (strength >= 60) return "Good";
    if (strength >= 40) return "Fair";
    return "Weak";
  };

  const getStrengthColor = () => {
    if (strength >= 80) return "bg-emerald-500";
    if (strength >= 60) return "bg-[#06402B]";
    if (strength >= 40) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getStrengthTextColor = () => {
    if (strength >= 80) return "text-emerald-600";
    if (strength >= 60) return "text-[#06402B]";
    if (strength >= 40) return "text-yellow-600";
    return "text-red-600";
  };

  const handleRegister = async () => {
    const newErrors = {
      email: validateEmail(email),
      password: validatePassword(password),
      confirmPassword: validateConfirmPassword(password, confirmPassword),
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) return;

    setLoading(true);
    setError("");

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) throw error;
      onRegisterSuccess(email);
    } catch (err) {
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSocialRegister = async (provider) => {
    setError("");
    setLoading(true);
    
    try {
      // Use the current origin for redirect
      const redirectUrl = window.location.origin;
      
      console.log("Starting OAuth with redirect:", redirectUrl);
      
      const { error } = await supabase.auth.signInWithOAuth({ 
        provider,
        options: {
          redirectTo: redirectUrl,
          skipBrowserRedirect: false,
        }
      });
      
      if (error) {
        console.error("OAuth error:", error);
        throw error;
      }
      
      // Browser will redirect, don't set loading to false
    } catch (error) {
      console.error(`Failed to register with ${provider}:`, error);
      setError(`Failed to register with ${provider}. Please try again.`);
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      headerRight={
        <div className="text-xs sm:text-sm text-gray-600">
          <span className="hidden sm:inline">Already have an account? </span>
          <button 
            onClick={onBack} 
            type="button"
            className="text-[#06402B] font-medium hover:underline"
          >
            Sign In
          </button>
        </div>
      }
    >
      <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-6 sm:py-8">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-5 sm:mb-6">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-800 mb-1 sm:mb-2">
              Create account
            </h1>
            <p className="text-sm sm:text-base text-gray-500">
              Join GiveAura today
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl text-xs sm:text-sm mb-4">
              {error}
            </div>
          )}

          <div className="space-y-4 sm:space-y-5">
            {/* Email Field */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl sm:rounded-[5px] border-2 border-gray-200 outline-none text-sm sm:text-base transition-colors"
                autoComplete="email"
              />
              {errors.email && (
                <p className="text-red-500 text-[10px] sm:text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Create a strong password"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-10 rounded-xl sm:rounded-[5px] border-2 border-gray-200  outline-none text-sm sm:text-base transition-colors"
                  autoComplete="new-password"
                />
                {password && strength >= 60 && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                  </span>
                )}
              </div>
              
              {/* Password Strength Indicator */}
              {password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[...Array(4)].map((_, i) => (
                      <div
                        key={i}
                        className={`h-1 sm:h-1.5 flex-1 rounded-full transition-colors ${
                          i < Math.ceil(strength / 25) ? getStrengthColor() : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-[10px] sm:text-xs text-gray-600">
                    Strength: <span className={`font-semibold ${getStrengthTextColor()}`}>{getStrengthLabel()}</span>
                  </p>
                </div>
              )}
              {errors.password && (
                <p className="text-red-500 text-[10px] sm:text-xs mt-1">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRegister()}
                  placeholder="Re-enter your password"
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 pr-10 rounded-xl sm:rounded-[5px] border-2 border-gray-200 outline-none text-sm sm:text-base transition-colors"
                  autoComplete="new-password"
                />
                {confirmPassword && password === confirmPassword && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-500">
                    <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                  </span>
                )}
              </div>
              {errors.confirmPassword && (
                <p className="text-red-500 text-[10px] sm:text-xs mt-1">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Register Button */}
            <button
              type="button"
              onClick={handleRegister}
              disabled={loading || !email || !password || !confirmPassword}
              className="w-full bg-[#06402B] text-white py-3 sm:py-3.5 rounded-xl sm:rounded-[5px] font-semibold text-sm sm:text-base hover:bg-[#053527] transition-colors disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.98]"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>

            {/* Divider */}
            <div className="relative my-4 sm:my-5">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-xs sm:text-sm">
                <span className="px-3 sm:px-4 bg-[#FBFCF6] text-gray-500">or register with</span>
              </div>
            </div>

            {/* Social Register */}
            <button
              type="button"
              onClick={() => handleSocialRegister("google")}
              disabled={loading}
              className="w-full px-4 py-2.5 sm:py-3 border-2 border-gray-200 rounded-xl sm:rounded-[5px] hover:bg-gray-50 transition-colors flex items-center justify-center gap-2 sm:gap-3 active:scale-[0.98] disabled:opacity-50"
            >
              <img
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt="Google"
                className="w-4 h-4 sm:w-5 sm:h-5"
              />
              <span className="text-sm sm:text-base font-medium text-gray-700">Google</span>
            </button>

            {/* Sign In Link - Mobile */}
            <p className="text-center text-xs sm:text-sm text-gray-600 sm:hidden pt-2">
              Already have an account?{' '}
              <button
                onClick={onBack}
                type="button"
                className="text-[#06402B] font-semibold hover:underline"
              >
                Sign In
              </button>
            </p>

            {/* Terms */}
            <p className="text-[10px] sm:text-xs text-center text-gray-500 pt-2">
              By creating an account, you agree to our{' '}
              <a href="/terms" className="text-[#06402B] hover:underline">Terms</a>
              {' '}and{' '}
              <a href="/privacy" className="text-[#06402B] hover:underline">Privacy Policy</a>
            </p>
          </div>
        </div>
      </main>
    </AuthLayout>
  );
}